<template>
  <div class="graffiti-subset">
    <!-- Toolbar -->
    <GraffitiToolbar
      @update:filters="handleFiltersUpdate"
      @update:view="handleViewUpdate"
      @export="handleExport"
      class="mb-6"
    />

    <!-- Chargement Skeleton -->
    <div
      v-if="loading"
      :class="[
        viewMode === 'card' ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6' : 'space-y-4',
      ]"
    >
      <SkeletonCard v-for="n in 6" :key="n" />
    </div>

    <!-- Contenu affiché (non chargé) -->
    <div v-else>
      <!-- Grille de cartes -->
      <div v-if="viewMode === 'card'" class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <div
          v-for="graffiti in displayedGraffiti"
          :key="graffiti.idGraf"
          @click="selectGraffiti(graffiti.idGraf)"
          class="cursor-pointer h-full"
          style="transform-origin: center"
        >
          <GraffitiCard :graffiti="graffiti" :is-list-view="false" class="h-full" />
        </div>
      </div>

      <!-- Vue Liste -->
      <div v-else-if="viewMode === 'list'" class="space-y-4">
        <div
          v-for="graffiti in displayedGraffiti"
          :key="graffiti.idGraf"
          @click="selectGraffiti(graffiti.idGraf)"
          class="cursor-pointer"
        >
          <GraffitiCard :graffiti="graffiti" :is-list-view="true" />
        </div>
      </div>

      <!-- Vue Tableau -->
      <div v-else-if="viewMode === 'table'">
        <GraffitiTable :graffiti-list="displayedGraffiti" @select-graffiti="selectGraffiti" />
      </div>

      <!-- Sentinel pour Infinite Scroll -->
      <div v-if="hasMoreToLoad && !loading" ref="sentinelRef" class="flex justify-center py-8">
        <div
          class="w-8 h-8 border-2 border-bdc-ocre border-t-transparent rounded-full animate-spin"
        ></div>
      </div>

      <!-- Message si aucun résultat -->
      <div
        v-if="!loading && displayedGraffiti.length === 0"
        class="text-center py-12 text-gray-500"
      >
        <p>Aucun graffiti ne correspond à vos critères de recherche.</p>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { GraffitiWithThemes } from '@/types'
import GraffitiCard from '@components/DataBrowser/GraffitiCard.vue'
import GraffitiTable from '@components/DataBrowser/GraffitiTable.vue'
import SkeletonCard from '@components/UI/SkeletonCard.vue'
import GraffitiToolbar from '@components/Viewer/GraffitiToolbar.vue'
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'

// Props
interface Props {
  graffitiList: GraffitiWithThemes[]
  loading?: boolean
  initialViewMode?: ViewMode
  initialFilters?: Filters | null
}

const props = withDefaults(defineProps<Props>(), {
  loading: false,
  initialViewMode: 'card',
  initialFilters: null,
})

// Émits
const emit = defineEmits<{
  'update:filters': [filters: Filters]
  'update:view': [mode: ViewMode]
  'select-graffiti': [id: number]
  export: []
}>()

// Types
type ViewMode = 'card' | 'list' | 'table'

interface Filters {
  sort: { by: string; direction: 'asc' | 'desc' }
  visible: boolean
  releves: string[]
  langues: string[]
  categories?: string[]
  thematiques?: string[]
  search: string
  mur: 'all' | 'Nord' | 'Sud'
  type: 'all' | 'figure' | 'texte'
}

// État local
const viewMode = ref<ViewMode>(props.initialViewMode)
const currentFilters = ref<Filters | null>(props.initialFilters)
const displayedGraffiti = ref<GraffitiWithThemes[]>([])
const filteredGraffiti = ref<GraffitiWithThemes[]>([...props.graffitiList])

// Infinite scroll pour optimiser le rendu
const itemsPerPage = 50 // Charger 50 graffitis à la fois
const loadedCount = ref(itemsPerPage) // Nombre d'items actuellement chargés
const sentinelRef = ref<HTMLElement | null>(null)
const observer = ref<IntersectionObserver | null>(null)

// Vérifier s'il reste des graffitis à charger
const hasMoreToLoad = computed(() => loadedCount.value < filteredGraffiti.value.length)

// Charger plus de graffitis
function loadMore() {
  const newCount = Math.min(loadedCount.value + itemsPerPage, filteredGraffiti.value.length)
  loadedCount.value = newCount
  displayedGraffiti.value = filteredGraffiti.value.slice(0, newCount)
}

// Mettre à jour les graffitis affichés quand loadedCount change
watch(loadedCount, () => {
  displayedGraffiti.value = filteredGraffiti.value.slice(0, loadedCount.value)
})

// Watch pour mettre à jour les données affichées quand la liste change
watch(
  () => props.graffitiList,
  newList => {
    applyFilters(newList)
  },
  { immediate: true }
)

// Setup Intersection Observer pour infinite scroll
onMounted(() => {
  observer.value = new IntersectionObserver(
    entries => {
      if (entries[0].isIntersecting && hasMoreToLoad.value) {
        loadMore()
      }
    },
    {
      root: null,
      rootMargin: '200px', // Commence à charger 200px avant d'atteindre le sentinel
      threshold: 0.01,
    }
  )

  // Observer le sentinel quand il est disponible
  watch(
    sentinelRef,
    newSentinel => {
      if (newSentinel && observer.value) {
        observer.value.observe(newSentinel)
      }
    },
    { immediate: true }
  )
})

onUnmounted(() => {
  observer.value?.disconnect()
})

// Fonction pour appliquer tous les filtres et le tri
function applyFilters(list: GraffitiWithThemes[] = props.graffitiList) {
  if (!currentFilters.value) {
    filteredGraffiti.value = [...list]
    loadedCount.value = itemsPerPage // Reset au premier lot
    displayedGraffiti.value = filteredGraffiti.value.slice(0, loadedCount.value)
    return
  }

  const filters = currentFilters.value
  let filtered = [...list]

  // Appliquer la recherche
  if (filters.search) {
    const query = filters.search.toLowerCase()
    filtered = filtered.filter(
      g =>
        g.idBdc.toLowerCase().includes(query) ||
        (g.type && g.type.toLowerCase().includes(query)) ||
        (g.categorie && g.categorie.toLowerCase().includes(query)) ||
        (g.description && g.description.toLowerCase().includes(query)) ||
        (g.commentaire && g.commentaire.toLowerCase().includes(query)) ||
        g.themes.some((t: string) => t.toLowerCase().includes(query))
    )
  }

  // Appliquer le filtre par Type
  if (filters.type !== 'all') {
    filtered = filtered.filter(g => {
      return g.type?.toLowerCase() === filters.type
    })
  }

  // Appliquer le filtre Visible
  if (filters.visible) {
    filtered = filtered.filter(g => g.autopsie)
  }

  // Appliquer le filtre des relevés
  if (filters.releves.length > 0) {
    filtered = filtered.filter(g => {
      if (filters.releves.includes('2017') && !g.releve2017) return false
      if (filters.releves.includes('2022') && !g.releve2022) return false
      return true
    })
  }

  // Appliquer le filtre des langues
  if (filters.langues.length > 0) {
    filtered = filtered.filter(g => {
      return g.langue && filters.langues.includes(g.langue)
    })
  }

  // Appliquer le filtre des catégories
  if (filters.categories && filters.categories.length > 0) {
    filtered = filtered.filter(g => {
      return g.categorie && filters.categories?.includes(g.categorie)
    })
  }

  // Appliquer le filtre des thématiques
  if (filters.thematiques && filters.thematiques.length > 0) {
    filtered = filtered.filter(g => {
      return g.themes.some((t: string) => filters.thematiques?.includes(t))
    })
  }

  // Appliquer le filtre du mur
  if (filters.mur !== 'all') {
    filtered = filtered.filter(g => g.mur === filters.mur)
  }

  // Appliquer le tri
  filtered.sort((a, b) => {
    const aValue = a[filters.sort.by as keyof GraffitiWithThemes] ?? null
    const bValue = b[filters.sort.by as keyof GraffitiWithThemes] ?? null

    if (aValue === null && bValue !== null) return filters.sort.direction === 'asc' ? 1 : -1
    if (aValue !== null && bValue === null) return filters.sort.direction === 'asc' ? -1 : 1
    if (aValue === null && bValue === null) return 0

    if (typeof aValue === 'string' && typeof bValue === 'string') {
      const aNum = parseInt(aValue.replace(/\D/g, ''), 10)
      const bNum = parseInt(bValue.replace(/\D/g, ''), 10)
      const aAlpha = aValue.replace(/\d/g, '')
      const bAlpha = bValue.replace(/\d/g, '')

      if (aAlpha === bAlpha) {
        return filters.sort.direction === 'asc' ? aNum - bNum : bNum - aNum
      }

      return filters.sort.direction === 'asc'
        ? aAlpha.localeCompare(bAlpha)
        : bAlpha.localeCompare(aAlpha)
    } else if (typeof aValue === 'number' && typeof bValue === 'number') {
      return filters.sort.direction === 'asc' ? aValue - bValue : bValue - aValue
    } else if (typeof aValue === 'boolean' && typeof bValue === 'boolean') {
      return filters.sort.direction === 'asc'
        ? aValue === bValue
          ? 0
          : aValue
            ? -1
            : 1
        : aValue === bValue
          ? 0
          : aValue
            ? 1
            : -1
    }

    const strA = String(aValue)
    const strB = String(bValue)
    return filters.sort.direction === 'asc' ? strA.localeCompare(strB) : strB.localeCompare(strA)
  })

  filteredGraffiti.value = filtered
  loadedCount.value = itemsPerPage // Reset au premier lot après filtrage
  displayedGraffiti.value = filtered.slice(0, loadedCount.value)
}

// Handlers
function handleFiltersUpdate(filters: Filters) {
  currentFilters.value = filters
  emit('update:filters', filters)
  applyFilters()
}

function handleViewUpdate(mode: ViewMode) {
  viewMode.value = mode
  emit('update:view', mode)
}

function selectGraffiti(id: number) {
  emit('select-graffiti', id)
}

function handleExport() {
  emit('export')
}
</script>

<style scoped>
.graffiti-subset {
  width: 100%;
}
</style>
